import { dataManager } from 'singletons';
import { fetchUser, fetchUsers } from 'actions/data';
import { setStatus, clearStatus } from 'actions/status';
import { AppDataState } from 'reducers/data';
import { StatusType } from 'types/status';
import UserDetails from 'types/userDetails';
import strings from 'utils/strings';
import { replaceTokens } from '@ps/payscale-shared-ui/lib/StringUtils';

export const resetUserPassword = (user: User, product: string): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            await dispatch(clearStatus());
            await dataManager.resetUserPassword(user.id, product);
            await dispatch(setStatus(
                StatusType.Success,
                strings.passwordResetSuccessTitle,
                replaceTokens(strings.passwordResetSuccessMsg, user.userName)));
        } catch {
            await dispatch(setStatus(
                StatusType.Error,
                strings.passwordResetFailureTitle,
                replaceTokens(strings.passwordResetFailureMsg, user.userName)));
        }
    };
};

export const unlockUserAccount = (user: User): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            await dispatch(clearStatus());
            await dataManager.unlockUserAccount(user.id);
            await dispatch(fetchUser(user.id));
            await dispatch(setStatus(
                StatusType.Success,
                strings.unlockUserSuccessTitle,
                replaceTokens(strings.unlockUserSuccessMsg, user.userName)));
        } catch {
            await dispatch(setStatus(
                StatusType.Error,
                strings.unlockUserFailureTitle,
                replaceTokens(strings.unlockUserFailureMsg, user.userName)));
        }
    };
};

export const inviteUser = (
    account: string,
    product: string,
    details: UserDetails,
    currentAccountDetails: MasterAccount|undefined,
    sendInvite: boolean):
    ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            await dispatch(clearStatus());
            await dataManager.inviteUser(account, product, details, sendInvite);
            await dispatch(fetchUsers(account));

            const title = (currentAccountDetails?.samlPartnerId || !sendInvite) ?
                strings.registerUserSuccessTitle : strings.inviteUserSuccessTitle;
            const msg = (currentAccountDetails?.samlPartnerId || !sendInvite) ?
                strings.registerUserSuccessMsg : strings.inviteUserSuccessMsg;

            await dispatch(setStatus(
                StatusType.Success,
                title,
                replaceTokens(msg, details.email)));
        } catch {
            const title = (currentAccountDetails?.samlPartnerId || !sendInvite) ?
                strings.registerUserFailureTitle : strings.inviteUserFailureTitle;
            const msg = (currentAccountDetails?.samlPartnerId || !sendInvite) ?
                strings.registerUserFailureMsg : strings.inviteUserFailureMsg;

            await dispatch(setStatus(
                StatusType.Error,
                title,
                replaceTokens(msg, details.email)));
        }
    };
};

export const deleteInvite = (account: string, user: User): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            await dispatch(clearStatus());
            await dataManager.deleteInvite(user.id);
            await dispatch(fetchUsers(account));
            // TODO: fetch pending users and update
            await dispatch(setStatus(
                StatusType.Success,
                strings.deleteInviteSuccessTitle,
                replaceTokens(strings.deleteInviteSuccessMsg, user.userName)));
        } catch (ex) {
            await dispatch(setStatus(
                StatusType.Error,
                strings.deleteInviteFailureTitle,
                replaceTokens(strings.deleteInviteFailureMsg, user.userName)));
        }
    };
};

export const inviteUsers = (
    account: string,
    product: string,
    users: UserDetails[],
    currentAccountDetails: MasterAccount|undefined,
    sendInvite: boolean):
ActionCreator<void> => {
    return async (dispatch: Function, getState: () => AppDataState): Promise<void> => {
        let successCount = 0;
        let failureCount = 0;

        await dispatch(clearStatus());

        // de-dupe users based on email address, keeping the first
        const seenEmails = new Set();

        // Add in anyone that already exists as seen so we don't send dupe ones
        getState().users?.forEach(u => seenEmails.add(u.userName));
        users = users.filter(user => {
            if (seenEmails.has(user.email)) {
                return false;
            }
            seenEmails.add(user.email);
            return true;
        });

        const roles = getState().roles;
        const managerRoles = roles
            .filter((role: UserRole) => role.name && role.name.toLowerCase() === 'manager')
            .map(role => role.id);

        // force all users to be just 'manager' role
        users.forEach(user => user.roleIds = managerRoles);

        await Promise.all(users.map(user => dataManager.inviteUser(account, product, user, sendInvite)
            .then(() => ++successCount)
            .catch(() => ++failureCount)));

        await dispatch(fetchUsers(account));
        // TODO: fetch pending users and update
        if (!failureCount) {
            const title = currentAccountDetails?.samlPartnerId ?
                strings.registerUsersSuccessTitle : strings.inviteUsersSuccessTitle;
            const msg = currentAccountDetails?.samlPartnerId ?
                strings.registerUsersSuccessMsg : strings.inviteUsersSuccessMsg;
            await dispatch(setStatus(
                StatusType.Success,
                title,
                replaceTokens(msg, successCount)));
        } else {
            const title = currentAccountDetails?.samlPartnerId ?
                strings.registerUsersFailureTitle : strings.inviteUsersFailureTitle;
            const msg = currentAccountDetails?.samlPartnerId ?
                strings.registerUsersFailureMsg : strings.inviteUsersFailureMsg;
            await dispatch(setStatus(
                StatusType.Error,
                title,
                replaceTokens(msg, failureCount, users.length)));
        }
    };
};

export const editUser = (account: string, userId: string, details: UserDetails): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        try {
            await dispatch(clearStatus());
            await dataManager.editUser(userId, account, details);

            // TODO: https://jira.underpaid.com/browse/SSO-482 we're reloading the whole thing now
            // because getting just the user (without including MAS doesn't give back names)
            // await dispatch(fetchUser(userId));
            await dispatch(fetchUsers(account));
            await dispatch(setStatus(
                StatusType.Success,
                strings.editUserSuccessTitle,
                replaceTokens(strings.editUserSuccessMsg, details.email)));
        } catch {
            await dispatch(setStatus(
                StatusType.Error,
                strings.editUserFailureTitle,
                replaceTokens(strings.editUserFailureMsg, details.email)));
        }
    };
};

export const deleteUser = (masterAccountId: string, user: User | null): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        if (!user) {
            return;
        }
        try {
            await dispatch(clearStatus());
            await dataManager.deleteUser(masterAccountId, user.id);
            await dispatch(setStatus(
                StatusType.Success,
                strings.deleteUserSuccessTitle,
                replaceTokens(strings.deleteUserSuccessMsg, user.userName)));
            await dispatch(fetchUsers);
        } catch {
            await dispatch(setStatus(
                StatusType.Error,
                strings.deleteUserFailureTitle,
                replaceTokens(strings.deleteUserFailureMsg, user.userName)));
        }
    };
};



// WEBPACK FOOTER //
// ./src/actions/user.ts